import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CheckboxHierarchyComponent } from '@app/components/forms/checkbox-hierarchy/checkbox-hierarchy.component';
import { Role } from '@app/models/account/role.model';
import { CompanyMFA } from '@app/models/company/settings/authentication/company-mfa.model';
import { PlatformModule } from '@app/platform.module';
import { AuthService, NotifyService } from '@app/services';
import OptionalObservable, { waitForOptionalObservable } from '@app/types/optional-observable';
import { TranslateModule } from '@ngx-translate/core';
import { Observable, Subject, from, of } from 'rxjs';
import { catchError, debounceTime, delay, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { RoleNameDisplayOptions } from '@app/types/translatables/role-display.options';

const CUSTOM_ROLES = 'CUSTOM';
const DEFAULT_ROLES = 'DEFAULT';

type ToggledRole = Role & { isMFAEnabled: boolean };

type GroupedRoles = {
    [DEFAULT_ROLES]: { [roleId: string]: ToggledRole };
    [CUSTOM_ROLES]: { [roleId: string]: ToggledRole };
};

type ToggleEvent = number | typeof CUSTOM_ROLES | typeof DEFAULT_ROLES | 'ALL';

@Component({
    selector: 'app-sign-in-mfa-setting',
    templateUrl: './mfa.template.html',
    standalone: true,
    imports: [CommonModule, PlatformModule, TranslateModule, FormsModule, CheckboxHierarchyComponent],
})
export class SignInMFASetting implements OnDestroy, OnInit {
    @Input() companyMFASetting: OptionalObservable<CompanyMFA | undefined>;
    @Input() roles: OptionalObservable<Role[] | undefined>;

    loadingLevel: ToggleEvent | null = null;
    error: 'LOADING_ERROR' | 'UPDATING_ERROR' | 'NOT_ENABLED' | null = null;

    activeMFARoles: GroupedRoles = { [DEFAULT_ROLES]: {}, [CUSTOM_ROLES]: {} };
    roleNameDisplayOptions = RoleNameDisplayOptions;

    private _companyMFASetting!: CompanyMFA;
    private _isMFAEnabled?: boolean = undefined;
    private _roles?: Role[] = undefined;
    private _onDestroy$ = new Subject<boolean>();
    private _onRoleToggle$ = new Subject<ToggleEvent>();

    constructor(
        private notifyService: NotifyService,
        private auth: AuthService
    ) {
        this.subscribeToToggleEvents();
    }

    ngOnInit(): void {
        this.loadingLevel = 'ALL';
        // Wait for the MFA Setting API request and Roles API request to return
        Promise.all([
            waitForOptionalObservable(this.companyMFASetting, this.setCompanyMFASetting.bind(this)),
            waitForOptionalObservable(this.roles, this.setRoles.bind(this)),
        ])
            .then(() => {
                // Parse the API results into the activeMFARoles map for easy consumption
                this.setActiveMFARoles();
            })
            .catch(() => (this.error = 'LOADING_ERROR'))
            .finally(() => {
                this.loadingLevel = null;
            });
    }

    ngOnDestroy(): void {
        this._onDestroy$.next();
        this._onDestroy$.complete();
    }

    get isMFAEnabled(): boolean | undefined {
        return this._isMFAEnabled;
    }
    set isMFAEnabled(isMFAEnabled: boolean | undefined) {
        this._isMFAEnabled = isMFAEnabled;
        // If we flip the global switch, we need to check/uncheck all the individual roles
        this.toggleAll(!!isMFAEnabled);
    }

    onRoleToggle(event: ToggleEvent): void {
        // Only send API request if not errored or loading (only ALL, since individual loading states can exist at once)
        if (!this.error && this.loadingLevel !== 'ALL') {
            this._onRoleToggle$.next(event);
        }
    }

    // Sets the private variable after API has returned
    private setCompanyMFASetting(companyMFASetting: CompanyMFA | undefined): void {
        if (!companyMFASetting) {
            // If the API returns with no value, the setting can't be loaded
            this.error = 'LOADING_ERROR';
            return;
        }
        this._companyMFASetting = companyMFASetting;
    }

    private setRoles(roles?: Role[]): void {
        if (!roles) {
            this.error = 'LOADING_ERROR';
            return;
        }
        this._roles = roles;

        // Add all role IDs to the initial map so that it can be populated
        this.activeMFARoles = roles.reduce((acc, cur) => {
            const roleType = cur.isImmutable ? DEFAULT_ROLES : CUSTOM_ROLES;
            acc[roleType][cur.id] = Object.assign(cur, { isMFAEnabled: false }); // Initialize all roles to false to begin with
            return acc;
        }, this.activeMFARoles);
    }

    // This function encapsulates the entire flow of toggling a role to be enabled/disabled
    private subscribeToToggleEvents(): void {
        let previousState: Role[];
        this._onRoleToggle$
            .pipe(
                takeUntil(this._onDestroy$),
                // We debounce the events because toggling a high level checkbox (ie. all default roles) can send multiple events
                // They'll be sent within the same change detection cycle, so a debounce time of 0 will group them into 1
                debounceTime(0),
                tap((event) => {
                    this.loadingLevel = event; // Can be ALL, Role Group (default or custom), or individual role
                    previousState = this._companyMFASetting.roles; // Used to revert model changes if an API error occurs
                }),
                map(() => this.getGroupToggled(DEFAULT_ROLES).concat(this.getGroupToggled(CUSTOM_ROLES))), // Get array of all enabled roles
                switchMap((roles) =>
                    from(this._companyMFASetting.setEnabledRoles(this.auth.company, roles)).pipe(
                        catchError(this.handleUpdateError.bind(this, previousState))
                    )
                ),
                tap((companyMFAResponse) => {
                    // Remove spinner
                    this.loadingLevel = null;
                    if (companyMFAResponse?.roles?.length === 0) {
                        // Flip global toggle if no child checkboxes are checked any longer
                        if (this._isMFAEnabled) {
                            this._isMFAEnabled = undefined; // set to undefined so that checkboxes are still visible
                        }
                    } else {
                        // Re-flip the global toggle if the user has checked at least 1 role
                        this._isMFAEnabled = true;
                    }
                })
            )
            .subscribe();
    }

    // Maps the roles into a grouped object, keyed by role ID, with isMFAEnabled denoting its current state
    // ie. activeMFARoles = {DEFAULT:{1: {... isMFAEnabled: true}, 2: {... isMFAEnabled: false}}, CUSTOM:{...}}
    private setActiveMFARoles(): void {
        this._companyMFASetting?.roles.forEach((activeMFARole) => {
            const roleType = activeMFARole.isImmutable ? DEFAULT_ROLES : CUSTOM_ROLES;
            this.activeMFARoles[roleType][activeMFARole.id].isMFAEnabled = true;
        });
        // flip the global toggle if any of the roles are enabled
        this._isMFAEnabled = this._companyMFASetting ? this._companyMFASetting.roles.length > 0 : false;
    }

    private toggleAll(isMFAEnabled: boolean): void {
        // Toggle each group asynchronously so they can be done in parallel
        Promise.all([this.toggleGroup(DEFAULT_ROLES, isMFAEnabled), this.toggleGroup(CUSTOM_ROLES, isMFAEnabled)]).then(
            () => this._onRoleToggle$.next('ALL') // Make API request to update BE setting
        );
    }

    // Minor optimization to make this async so that each group can be run in parallel
    private async toggleGroup(groupType: keyof GroupedRoles, isMFAEnabled: boolean): Promise<void> {
        for (const role in this.activeMFARoles[groupType]) {
            this.activeMFARoles[groupType][role].isMFAEnabled = isMFAEnabled;
        }
    }

    private getGroupToggled(groupType: keyof GroupedRoles): Role[] {
        return Object.values(this.activeMFARoles[groupType]).filter((role) => role.isMFAEnabled);
    }

    private handleUpdateError(previousState: Role[]): Observable<void> {
        return of(void 0).pipe(
            tap(() => {
                // Set a temporary error to ensure we don't try to send more API calls while reverting the model
                this.error = 'UPDATING_ERROR';
                this.notifyService.error('settings.signIn.mfa.updatingFailedErrorMessage');
                // reset model to before API call
                this._companyMFASetting.roles = previousState;
                this.setRoles(this._roles);
                this.setActiveMFARoles();
            }),
            // After the DOM has returned to a stable state we can clear the error and allow the user to try again
            delay(0),
            tap(() => (this.error = null))
        );
    }
}
